import { useMemo } from "react";
import { FileText, Hash, Text } from "lucide-react";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { docSections, DocSection, DocHeading } from "./docs-content";

type SearchResult =
  | { type: "section"; section: DocSection }
  | { type: "heading"; section: DocSection; heading: DocHeading }
  | { type: "content"; section: DocSection; snippet: string };

interface DocsSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: (sectionId: string, headingId?: string) => void;
}

function getSnippet(
  content: string,
  query: string,
  contextLength = 60,
): string {
  const lowerContent = content.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerContent.indexOf(lowerQuery);

  if (index === -1) return "";

  const start = Math.max(0, index - contextLength);
  const end = Math.min(content.length, index + query.length + contextLength);

  let snippet = content.slice(start, end);

  // Clean up markdown syntax from snippet
  snippet = snippet
    .replace(/```[\s\S]*?```/g, "") // Remove code blocks
    .replace(/`[^`]+`/g, "") // Remove inline code
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // Convert links to text
    .replace(/[#*_~]/g, "") // Remove markdown formatting
    .replace(/\n+/g, " ") // Replace newlines with spaces
    .trim();

  const prefix = start > 0 ? "..." : "";
  const suffix = end < content.length ? "..." : "";

  return `${prefix}${snippet}${suffix}`;
}

function searchDocs(query: string): SearchResult[] {
  if (!query.trim()) return [];

  const lowerQuery = query.toLowerCase().trim();
  const results: SearchResult[] = [];

  for (const section of docSections) {
    // Search section titles
    if (section.title.toLowerCase().includes(lowerQuery)) {
      results.push({ type: "section", section });
    }

    // Search headings
    for (const heading of section.headings) {
      if (heading.text.toLowerCase().includes(lowerQuery)) {
        results.push({ type: "heading", section, heading });
      }
    }

    // Search content (only if not already matched by title)
    if (
      !section.title.toLowerCase().includes(lowerQuery) &&
      section.rawContent.toLowerCase().includes(lowerQuery)
    ) {
      const snippet = getSnippet(section.rawContent, query);
      if (snippet) {
        results.push({ type: "content", section, snippet });
      }
    }
  }

  return results;
}

export function DocsSearch({
  open,
  onOpenChange,
  onNavigate,
}: DocsSearchProps) {
  // When no query, show all sections as suggestions
  const defaultResults = useMemo(
    () => docSections.map((section) => ({ type: "section" as const, section })),
    [],
  );

  const handleSelect = (result: SearchResult) => {
    if (result.type === "heading") {
      onNavigate(result.section.id, result.heading.id);
    } else {
      onNavigate(result.section.id);
    }
    onOpenChange(false);
  };

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Search Documentation"
      description="Search through all documentation pages and headings"
      showCloseButton={false}
    >
      <CommandInput placeholder="Search documentation..." />
      <CommandList className="max-h-[400px]">
        <CommandEmpty>No results found.</CommandEmpty>

        {/* Sections group */}
        <CommandGroup heading="Pages">
          {docSections.map((section) => (
            <CommandItem
              key={`section-${section.id}`}
              value={`section ${section.title}`}
              onSelect={() => handleSelect({ type: "section", section })}
              className="flex items-center gap-3 cursor-pointer"
            >
              <FileText className="h-4 w-4 text-zinc-400 shrink-0" />
              <span>{section.title}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        {/* Headings group */}
        <CommandGroup heading="Headings">
          {docSections.flatMap((section) =>
            section.headings.map((heading) => (
              <CommandItem
                key={`heading-${section.id}-${heading.id}`}
                value={`heading ${heading.text} ${section.title}`}
                onSelect={() =>
                  handleSelect({ type: "heading", section, heading })
                }
                className="flex items-center gap-3 cursor-pointer"
              >
                <Hash className="h-4 w-4 text-zinc-400 shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="truncate">{heading.text}</span>
                  <span className="text-xs text-zinc-500 truncate">
                    in {section.title}
                  </span>
                </div>
              </CommandItem>
            )),
          )}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
